import { PetType } from 'constants/pet-type';
import {
  AgeSegmentationIds,
  AllAllergySegmentationIds,
  BreedSegmentationIds,
  CatNutritionSegmentationIds,
  NutritionSegmentationIds,
  ProteinTagValues,
  SegmentationIds,
} from 'constants/segmentation';
import { usePetContext } from 'contexts/pet';
import { usePetProfile } from 'contexts/pet-profile';
import {
  InputProductType,
  WetFoodTagValues,
} from 'interfaces/api-input/input-product-type';
import { possibleTags } from 'interfaces/collection-filter';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { isCatProfile, isDogProfile } from 'types/pet-profile';
import { useFilteredNeeds } from '../../../../../hooks/use-filtered-needs';
import { Action, DraftState, State } from './interface';
import { draftStateReducer, getInitialState, reducer } from './utils';

interface NeedCaptureContextType {
  activePetType?: (typeof PetType)[keyof typeof PetType];
  draftData: DraftState;
  allergyEntries: Array<[string, AllAllergySegmentationIds]>;
  filteredNeedsEntries: Array<
    [string, NutritionSegmentationIds | CatNutritionSegmentationIds]
  >;
  hasInteracted: boolean;
  state: State;
  dispatch: React.Dispatch<Action>;
  handleClose: () => void;
  handleCloseFoodTypeAndTasteSelector: () => void;
  handleCloseNeeds: () => void;
  handleConfirmFoodTypeAndTaste: () => void;
  handleConfirmNeeds: () => void;
  handleSelectAllergies: (value: AllAllergySegmentationIds) => void;
  handleSelectFoodTypes: (value: InputProductType) => void;
  handleSelectNeeds: (
    value: NutritionSegmentationIds | CatNutritionSegmentationIds
  ) => void;
  handleSelectTastePreferences: (
    value: ProteinTagValues | possibleTags
  ) => void;
  handleSelectWetFoodTypes: (values: WetFoodTagValues[]) => void;
  onSelectAge: (value: AgeSegmentationIds) => void;
  onSelectBreed: (value: BreedSegmentationIds) => void;
}

const NeedCaptureContext = createContext<NeedCaptureContextType>(
  {} as NeedCaptureContextType
);

/**
 * Provider for the need capture context.
 */
export const NeedCaptureProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { current } = usePetContext();
  const [hasInteracted, setHasInteracted] = useState(false);
  const { petProfile, setPetProfile } = usePetProfile();

  const initialState = getInitialState(
    current === 'dogs' ? PetType.Dog : PetType.Cat
  );

  const activePetType = useMemo(() => {
    if (petProfile?.petType === 'dog' && current === 'dogs') {
      return PetType.Dog;
    }
    if (petProfile?.petType === 'cat' && current === 'cats') {
      return PetType.Cat;
    }
    return undefined;
  }, [petProfile?.petType, current]);

  const { allergyEntries, filteredNeedsEntries } = useFilteredNeeds({
    petType: activePetType ?? PetType.Dog,
  });

  const [state, dispatch] = useReducer(reducer, initialState);

  // Reset state when petType changes (e.g., switching from dog to cat page)
  useEffect(() => {
    const newPetType = current === 'dogs' ? PetType.Dog : PetType.Cat;
    if (state.petType && state.petType !== newPetType) {
      dispatch({ type: 'RESET_STATE', payload: newPetType });
    }
  }, [current, state.petType]);

  // Update state when petProfile changes
  useEffect(() => {
    if (petProfile) {
      dispatch({
        type: 'SET_MULTIPLE',
        payload: {
          selectedAge: petProfile.selectedAge,
          selectedBreed: isDogProfile(petProfile)
            ? petProfile.selectedBreed
            : undefined,
          selectedNeeds: petProfile.selectedNeeds ?? [],
          selectedAllergies: petProfile.selectedAllergies ?? [],
          selectedFoodTypes: petProfile.selectedFoodTypes ?? [],
          selectedWetFoodTypes: isCatProfile(petProfile)
            ? petProfile.selectedWetFoodTypes
            : [],
          selectedTastePreferences: isCatProfile(petProfile)
            ? petProfile.selectedTastePreferences
            : [],
          lastTouched: petProfile.lastTouched,
          petType: petProfile.petType,
        },
      });
    }
  }, [petProfile]);

  const initialDraftData: DraftState = {
    draftNeeds: [],
    draftAllergies: [],
    draftFoodTypes: [],
    draftWetFoodTypes: [],
    draftTastePreferences: [],
  };

  const [draftData, dispatchTemporaryData] = useReducer(
    draftStateReducer,
    initialDraftData
  );

  // Synchronize draft data with state changes
  useEffect(() => {
    dispatchTemporaryData({
      type: 'SET_MULTIPLE',
      payload: {
        draftNeeds: state.selectedNeeds,
        draftAllergies: state.selectedAllergies,
        draftFoodTypes: state.selectedFoodTypes,
        draftWetFoodTypes: state.selectedWetFoodTypes,
        draftTastePreferences: state.selectedTastePreferences,
        lastTouched: state.lastTouched as SegmentationIds | undefined,
      },
    });
  }, [
    state.selectedNeeds,
    state.selectedAllergies,
    state.selectedFoodTypes,
    state.selectedWetFoodTypes,
    state.selectedTastePreferences,
    state.lastTouched,
  ]);

  const handleSelectNeeds = useCallback(
    (value: NutritionSegmentationIds | CatNutritionSegmentationIds): void => {
      if (draftData.draftNeeds.includes(value)) {
        const newNeeds = draftData.draftNeeds.filter((item) => item !== value);
        dispatchTemporaryData({
          type: 'SET_DRAFT_NEEDS',
          payload: newNeeds,
        });

        if (
          value === NutritionSegmentationIds.Intolerance ||
          value === CatNutritionSegmentationIds.IntoleranceCat
        ) {
          dispatchTemporaryData({
            type: 'SET_DRAFT_ALLERGIES',
            payload: [],
          });
        }
      } else {
        dispatchTemporaryData({
          type: 'SET_DRAFT_NEEDS',
          payload: [...draftData.draftNeeds, value],
        });
      }
    },
    [draftData.draftNeeds]
  );

  const onSelectAge = useCallback(
    (value: AgeSegmentationIds): void => {
      setHasInteracted(true);
      dispatch({ type: 'SET_AGE', payload: value });
      setPetProfile({
        ...petProfile,
        ...(petProfile.selectedAge !== value && { lastTouched: value }),
        selectedAge: petProfile.selectedAge === value ? undefined : value,
      });
      dispatch({ type: 'TOGGLE_AGE_SELECTOR' });
    },
    [petProfile, setPetProfile]
  );

  const onSelectBreed = useCallback(
    (value: BreedSegmentationIds): void => {
      if (!isDogProfile(petProfile)) return;
      setHasInteracted(true);
      dispatch({ type: 'SET_BREED', payload: value });
      setPetProfile({
        ...petProfile,
        ...(petProfile.selectedBreed !== value && { lastTouched: value }),
        selectedBreed: petProfile.selectedBreed === value ? undefined : value,
      });
      dispatch({ type: 'TOGGLE_BREED_SELECTOR' });
    },
    [petProfile, setPetProfile]
  );

  const handleSelectAllergies = useCallback(
    (value: AllAllergySegmentationIds): void => {
      if (draftData.draftAllergies.includes(value)) {
        dispatchTemporaryData({
          type: 'SET_DRAFT_ALLERGIES',
          payload: draftData.draftAllergies.filter((item) => item !== value),
        });
      } else {
        dispatchTemporaryData({
          type: 'SET_DRAFT_ALLERGIES',
          payload: [...draftData.draftAllergies, value],
        });
      }
    },
    [draftData.draftAllergies]
  );

  const handleCloseNeeds = useCallback(() => {
    dispatchTemporaryData({
      type: 'SET_DRAFT_NEEDS',
      payload: state?.selectedNeeds ?? [],
    });
    dispatchTemporaryData({
      type: 'SET_DRAFT_ALLERGIES',
      payload: state?.selectedAllergies ?? [],
    });
    dispatch({ type: 'TOGGLE_NEED_SELECTOR' });
  }, [state?.selectedNeeds, state?.selectedAllergies]);

  const handleConfirmNeeds = useCallback(() => {
    setHasInteracted(true);
    dispatch({
      type: 'SET_NEEDS',
      payload: draftData.draftNeeds,
    });
    dispatch({
      type: 'SET_ALLERGIES',
      payload: draftData.draftAllergies,
    });
    setPetProfile({
      ...petProfile,
      selectedNeeds: draftData.draftNeeds,
      selectedAllergies: draftData.draftAllergies,
      lastTouched: draftData.lastTouched,
    });
    dispatch({ type: 'TOGGLE_NEED_SELECTOR' });
  }, [
    draftData.draftAllergies,
    draftData.draftNeeds,
    draftData.lastTouched,
    petProfile,
    setPetProfile,
  ]);

  const handleSelectFoodTypes = useCallback(
    (value: InputProductType): void => {
      const currentTypes = draftData.draftFoodTypes ?? [];
      const newTypes = currentTypes.includes(value)
        ? currentTypes.filter((type) => type !== value)
        : [...currentTypes, value];
      dispatchTemporaryData({
        type: 'SET_DRAFT_FOOD_TYPES',
        payload: newTypes,
      });
    },
    [draftData.draftFoodTypes]
  );

  const handleSelectWetFoodTypes = useCallback(
    (values: WetFoodTagValues[]): void => {
      dispatchTemporaryData({
        type: 'SET_DRAFT_WET_FOOD_TYPES',
        payload: values,
      });
    },
    []
  );
  const handleSelectTastePreferences = useCallback(
    (value: ProteinTagValues | possibleTags): void => {
      const currentTastes = draftData.draftTastePreferences ?? [];
      const newTastes = currentTastes.includes(value as ProteinTagValues)
        ? currentTastes.filter((taste) => taste !== value)
        : [...currentTastes, value as ProteinTagValues];
      dispatchTemporaryData({
        type: 'SET_DRAFT_TASTE_PREFERENCES',
        payload: newTastes,
      });
    },
    [draftData.draftTastePreferences]
  );

  const handleCloseFoodTypeAndTasteSelector = useCallback(() => {
    dispatchTemporaryData({
      type: 'SET_DRAFT_FOOD_TYPES',
      payload: state?.selectedFoodTypes ?? [],
    });
    dispatchTemporaryData({
      type: 'SET_DRAFT_WET_FOOD_TYPES',
      payload: state?.selectedWetFoodTypes ?? [],
    });
    dispatchTemporaryData({
      type: 'SET_DRAFT_TASTE_PREFERENCES',
      payload: state?.selectedTastePreferences ?? [],
    });
    dispatch({ type: 'TOGGLE_FOOD_TYPE_AND_TASTE_SELECTOR' });
  }, [
    state?.selectedFoodTypes,
    state?.selectedWetFoodTypes,
    state?.selectedTastePreferences,
  ]);

  const handleConfirmFoodTypeAndTaste = useCallback(() => {
    // If all food types are deselected, select all types
    const foodTypesToSet =
      draftData.draftFoodTypes.length === 0
        ? [
            InputProductType.WetFood,
            InputProductType.DryFood,
            InputProductType.Snacks,
          ]
        : draftData.draftFoodTypes;

    const wetFoodTypesToSet =
      draftData.draftFoodTypes.length === 0
        ? Object.values(WetFoodTagValues)
        : draftData.draftWetFoodTypes;

    dispatch({
      type: 'SET_FOOD_TYPES',
      payload: foodTypesToSet,
    });
    dispatch({
      type: 'SET_WET_FOOD_TYPES',
      payload: wetFoodTypesToSet,
    });
    dispatch({
      type: 'SET_TASTE_PREFERENCE_PREFERENCES',
      payload: draftData.draftTastePreferences,
    });
    dispatch({ type: 'TOGGLE_FOOD_TYPE_AND_TASTE_SELECTOR' });
    setPetProfile({
      ...petProfile,
      selectedFoodTypes: foodTypesToSet,
      ...(isCatProfile(petProfile) && {
        selectedWetFoodTypes: wetFoodTypesToSet,
        selectedTastePreferences: draftData.draftTastePreferences,
      }),
      lastTouched: draftData.lastTouched,
    });
  }, [
    draftData.draftFoodTypes,
    draftData.draftTastePreferences,
    draftData.draftWetFoodTypes,
    draftData.lastTouched,
    petProfile,
    setPetProfile,
  ]);

  const handleClose = useCallback(() => {
    if (state.isAgeSelectorOpen) dispatch({ type: 'TOGGLE_AGE_SELECTOR' });
    if (state.isBreedSelectorOpen) dispatch({ type: 'TOGGLE_BREED_SELECTOR' });
    if (state.isFoodTypeAndTasteSelectorOpen)
      handleCloseFoodTypeAndTasteSelector();
    if (state.isNeedSelectorOpen) handleCloseNeeds();
  }, [
    state.isAgeSelectorOpen,
    state.isBreedSelectorOpen,
    state.isFoodTypeAndTasteSelectorOpen,
    state.isNeedSelectorOpen,
    handleCloseFoodTypeAndTasteSelector,
    handleCloseNeeds,
  ]);

  const value = useMemo(() => {
    return {
      activePetType,
      allergyEntries,
      draftData,
      filteredNeedsEntries,
      hasInteracted,
      state,
      dispatch,
      handleClose,
      handleCloseFoodTypeAndTasteSelector,
      handleCloseNeeds,
      handleConfirmFoodTypeAndTaste,
      handleConfirmNeeds,
      handleSelectAllergies,
      handleSelectFoodTypes,
      handleSelectNeeds,
      handleSelectTastePreferences,
      handleSelectWetFoodTypes,
      onSelectAge,
      onSelectBreed,
    };
  }, [
    activePetType,
    allergyEntries,
    draftData,
    filteredNeedsEntries,
    hasInteracted,
    state,
    handleClose,
    handleCloseFoodTypeAndTasteSelector,
    handleCloseNeeds,
    handleConfirmFoodTypeAndTaste,
    handleConfirmNeeds,
    handleSelectAllergies,
    handleSelectFoodTypes,
    handleSelectNeeds,
    handleSelectTastePreferences,
    handleSelectWetFoodTypes,
    onSelectAge,
    onSelectBreed,
  ]);

  return (
    <NeedCaptureContext.Provider value={value}>
      {children}
    </NeedCaptureContext.Provider>
  );
};

export const useNeedCapture = (): NeedCaptureContextType =>
  useContext(NeedCaptureContext);
